import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import { useSelector, useDispatch } from "react-redux";
import { actions } from "../../../util/actions";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { showErrorModal } from "../../../util/general";

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 800;
`;

const Modal = styled.div`
  background: #FFF;
  border-radius: 5px;
  box-shadow: 2px 5px 15px rgba(0,0,0,0.3);
  padding: 1.5em;
  width: 90%;
  max-width: 780px;
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  color: ${({ colors }) => colors.labelText};
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1em;
  margin-bottom: 1em;
  & > div { flex: 1; }
`;

const Field = styled.div`
  label {
    display: block;
    margin-bottom: 0.3em;
    font-weight: 600;
    color: ${({ colors }) => colors.labelText};
  }
  input, select {
    width: 100%;
    padding: 0.4em;
    font-size: 1em;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    background: #fff;
  }
`;

const ImplSection = styled.div`
  background: #f5f5f5;
  padding: 1em;
  border-radius: 4px;
  margin-bottom: 1em;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 1em;
  margin-top: 0.5em;
  margin-left: 0.5em;
  overflow-x: auto;
  label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25em;
    font-size: 0.9em;
    padding: 0.1em 0.25em;
    color: ${({ colors }) => colors.labelText};
    white-space: nowrap;
  }
  input {
    margin: 0;
  }
`;

const GradesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  margin: 0.5em 0;
  label {
    margin: 10;
    font-weight: 600;
    color: ${({ colors }) => colors.labelText};
  }
`;

const SelectAll = styled.span`
  color: #f6a828;
  background: transparent;
  border: none;
  padding: 0.2em 0.5em;
  border-radius: 2px;
  cursor: pointer;
  font-size: 0.9em;
  text-decoration: underline;
  font-weight: 600;
`;

const DateSelect = styled.div`
  display: flex;
  gap: 0.5em;
  select { flex: 1; }
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
  margin-top: 1.5em;
`;

const BackButton = styled.button`
  padding: 0.6em 1.2em;
  font-size: 1em;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.labelText};
  border: 1px solid ${({ colors }) => colors.border};
`;

const SaveButton = styled.button`
  padding: 0.6em 1.2em;
  font-size: 1em;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  border: none;
`;

const gradeKeys = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];

const buildYearOptions = (selectedYear) => {
  const currentYear = new Date().getFullYear();
  const years = new Set();

  for (let i = 0; i <= 10; i += 1) {
    years.add(currentYear + i); // future (including current)
  }
  for (let i = 1; i <= 10; i += 1) {
    years.add(currentYear - i); // past for continuity
  }

  if (selectedYear && !Number.isNaN(Number(selectedYear))) {
    years.add(Number(selectedYear));
  }

  return Array.from(years)
    .sort((a, b) => b - a)
    .map(String);
};

export default function EditTeacherModal({
  teacher,
  colors,
  onCancel,
  onSave
}) {
  const current = useSelector(s => s.auth.selected_subscriptions);
  const user_id = useSelector(s => s.auth.user.user_id);
  const schoolsCache = useSelector(s => s.auth.subscription_schools || {});
  const cachedSchoolLevels = useSelector(s => s.auth.school_levels);
  const dispatch = useDispatch();
  const normalizeId = value =>
    value === undefined || value === null ? "" : String(value).trim();

  const teacherSchoolId = normalizeId(teacher?.org_id ?? teacher?.school_id ?? "");
  const teacherSchoolLevel = normalizeId(teacher?.school_level || "");
  const teacherOrgName =
    teacher?.org_name || teacher?.org_display || teacher?.school_name || "";
  const teacherKey = teacher?.implementation_id;

  const teacherDisplayName = (typeof teacherOrgName === "string"
    ? teacherOrgName.trim()
    : teacherOrgName) || teacherSchoolId;

  const fallbackSchools = teacherSchoolId
    ? [
        {
          org_id: teacherSchoolId,
          org_display: teacherDisplayName,
          org_name: teacherDisplayName,
        },
      ]
    : [];

  const fallbackLevels = teacherSchoolLevel
    ? [
        {
          school_level: teacherSchoolLevel,
          school_level_desc: teacherSchoolLevel,
        },
      ]
    : [];

  // form state
  const [schools, setSchools] = useState(fallbackSchools);
  const [schoolLevels, setSchoolLevels] = useState(fallbackLevels);
  const [email, setEmail]     = useState(teacher.email || "");
  const [students, setStudents] = useState(teacher.num_of_students || "");
  const [schoolId, setSchoolId] = useState(teacherSchoolId);
  const [schoolLevel, setSchoolLevel] = useState(teacherSchoolLevel);
  const [gradesState, setGradesState] = useState(
    gradeKeys.reduce((acc,g) => ({
      ...acc,
      [g]: (teacher.grade_records||[]).some(r=>r.grade_code===g)
    }), {})
  );
  const parseDate = iso => {
    const [y,m,d] = (iso||"").split("-");
    return { year:y, month:m, day:d };
  };
  const [startDate, setStartDate] = useState(parseDate(teacher.start_date));
  const [endDate,   setEndDate]   = useState(parseDate(teacher.end_date));
  const startYearOptions = useMemo(() => buildYearOptions(startDate.year), [startDate.year]);
  const endYearOptions = useMemo(() => buildYearOptions(endDate.year), [endDate.year]);

  const normalizeSchool = school => {
    if (!school || typeof school !== "object") return school;
    const orgId =
      normalizeId(school.org_id ?? school.school_id ?? school.orgId ?? school.orgID);
    const display =
      school.org_display ??
      school.org_name ??
      school.school_name ??
      school.name ??
      "";
    const name =
      school.org_name ??
      school.org_display ??
      school.school_name ??
      school.name ??
      display;
    return {
      ...school,
      org_id: orgId,
      org_display: display,
      org_name: name,
    };
  };

  const dedupeSchools = list => {
    const seen = new Set();
    return list.filter(item => {
      const id = normalizeId(item?.org_id ?? item?.school_id);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const normalizeSchools = list =>
    dedupeSchools(Array.isArray(list) ? list.map(normalizeSchool) : []);

  const ensureTeacherSchool = list => {
    const normalized = normalizeSchools(list);
    const currentOrgId = normalizeId(teacherSchoolId);
    if (!currentOrgId) return normalized;
    const exists = normalized.some(
      s => normalizeId(s.org_id ?? s.school_id) === currentOrgId
    );
    if (exists) return normalized;
    const fallbackName = (typeof teacherOrgName === "string"
      ? teacherOrgName.trim()
      : teacherOrgName) || currentOrgId;
    return [
      ...normalized,
      normalizeSchool({
        org_id: teacherSchoolId,
        org_display: fallbackName,
        org_name: fallbackName,
      }),
    ];
  };

  const normalizeLevel = level => {
    if (!level || typeof level !== "object") return level;
    const code = normalizeId(level.school_level);
    const desc = typeof level.school_level_desc === "string"
      ? level.school_level_desc.trim()
      : level.school_level_desc;
    return {
      ...level,
      school_level: code,
      school_level_desc: desc ?? code,
    };
  };

  const ensureTeacherLevel = levelsList => {
    const base = Array.isArray(levelsList) ? levelsList.map(normalizeLevel) : [];
    const seen = new Set();
    const deduped = base.filter(level => {
      const code = normalizeId(level?.school_level);
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
    const teacherCode = normalizeId(teacherSchoolLevel);
    if (!teacherCode) return deduped;
    if (seen.has(teacherCode)) return deduped;
    return [
      ...deduped,
      {
        school_level: teacherCode,
        school_level_desc: teacherSchoolLevel?.trim?.() || teacherCode,
      },
    ];
  };

  useEffect(() => {
    setEmail(teacher.email || "");
    setStudents(teacher.num_of_students || "");
    const nextSchoolId = teacher?.org_id ?? teacher?.school_id ?? "";
    setSchoolId(normalizeId(nextSchoolId));
    setSchoolLevel(normalizeId(teacher?.school_level || ""));
    setGradesState(
      gradeKeys.reduce((acc, g) => ({
        ...acc,
        [g]: (teacher.grade_records || []).some(r => r.grade_code === g),
      }), {})
    );
    setStartDate(parseDate(teacher.start_date));
    setEndDate(parseDate(teacher.end_date));
  }, [teacherKey]);

  // confirmation modals
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modalTitle, setModalTitle]   = useState("");
  const [modalMsg, setModalMsg]       = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [resultOpen, setResultOpen]   = useState(false);
  const [modalStatus, setModalStatus] = useState("Success");

  useEffect(() => {
    const stateCode = current?.org_state;
    if (!stateCode) {
      setSchools(ensureTeacherSchool([]));
      return;
    }

    if (Object.prototype.hasOwnProperty.call(schoolsCache, stateCode)) {
      setSchools(ensureTeacherSchool(schoolsCache[stateCode]));
      return;
    }

    if (!current?.invoice_id) {
      setSchools(ensureTeacherSchool([]));
      return;
    }

    let cancelled = false;
    API.getSubscriptionSchools({ state: stateCode })
      .then(resp => {
        if (cancelled) return;
        const normalizedList = normalizeSchools(resp?.schools);
        const withTeacher = ensureTeacherSchool(normalizedList);
        setSchools(withTeacher);
        dispatch({
          type: actions.STORE_SUBSCRIPTION_SCHOOLS,
          schoolsByState: { [stateCode]: normalizedList },
        });
      })
      .catch(err => {
        if (!cancelled) {
          console.error("Failed to load schools:", err);
          setSchools(ensureTeacherSchool([]));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    current?.invoice_id,
    current?.org_state,
    schoolsCache,
    teacherKey,
    teacherSchoolId,
    teacherOrgName,
    dispatch,
  ]);

  useEffect(() => {
    if (Array.isArray(cachedSchoolLevels)) {
      setSchoolLevels(ensureTeacherLevel(cachedSchoolLevels));
      return;
    }

    let cancelled = false;
    API.getSchoolLevels()
      .then(resp => {
        if (cancelled) return;
        const levels = Array.isArray(resp?.school_levels) ? resp.school_levels : [];
        setSchoolLevels(ensureTeacherLevel(levels));
        dispatch({
          type: actions.STORE_SCHOOL_LEVELS,
          schoolLevels: levels,
        });
      })
      .catch(err => {
        if (!cancelled) {
          console.error("Failed to load school levels:", err);
          setSchoolLevels(ensureTeacherLevel([]));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cachedSchoolLevels, dispatch, teacherSchoolLevel]);

  const toggleGrade = g =>
    setGradesState(gs => ({ ...gs, [g]: !gs[g] }));

  const selectAllGrades = () => {
    setGradesState(gradeKeys.reduce((acc,g)=>(acc[g]=true,acc), {}));
  };

  const buildPayload = () => {
    const selectedSchool =
      schools?.find(
        s => String(s.org_id ?? s.school_id) === String(schoolId)
      ) || {};

    const resolvedOrgId =
      selectedSchool.org_id ?? schoolId ?? teacher?.org_id ?? "";
    const resolvedSchoolId =
      selectedSchool.school_id ?? teacher?.school_id ?? "";
    const resolvedOrgNameRaw =
      selectedSchool.org_name ||
      selectedSchool.org_display ||
      teacher?.org_name ||
      teacher?.org_display ||
      "";
    const resolvedOrgName =
      typeof resolvedOrgNameRaw === "string"
        ? resolvedOrgNameRaw.trim()
        : resolvedOrgNameRaw;

    const ensureDate = (date, fallback) => {
      const base = fallback instanceof Date ? fallback : new Date();
      const year = date.year || String(base.getFullYear());
      const month = date.month || String(base.getMonth() + 1).padStart(2, "0");
      const day = date.day || String(base.getDate()).padStart(2, "0");
      return {
        year: String(year).padStart(4, "0"),
        month: String(month).padStart(2, "0"),
        day: String(day).padStart(2, "0"),
      };
    };

    const normalizedStart = ensureDate(startDate, new Date());
    const normalizedEnd = ensureDate(endDate, new Date());

    return {
      email,
      implementation_id: teacher.implementation_id,
      customer_user_id: teacher.customer_user_id,
      user_id:teacher.user_id ,
      num_of_students: students,
      invoice_id: current?.invoice_id,
      school_level: schoolLevel,
      school_id: normalizeId(resolvedSchoolId).trim(),
      org_id:
        typeof resolvedOrgId === "string"
          ? resolvedOrgId.trim()
          : resolvedOrgId,
      org_name: resolvedOrgName,
      start_date: `${normalizedStart.year}-${normalizedStart.month}-${normalizedStart.day}`,
      end_date: `${normalizedEnd.year}-${normalizedEnd.month}-${normalizedEnd.day}`,
      grades: gradeKeys.filter(g => gradesState[g]),
    };
  };

  const handleSaveClick = e => {
    e.preventDefault();
    const p = buildPayload();
    if (!p.email||!p.school_id||!p.school_level||!p.grades.length) {
      showErrorModal("Please fill out all required fields.");
      return;
    }
    setPendingPayload(p);
    setModalTitle("Confirm Changes");
    setModalMsg("Are you sure you want to save these changes?");
    setModalStatus("Success");
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    setModalStatus("Success");
    API.editSubscriptionTeacher(pendingPayload)
      .then(r => {
        const status = r?.status === "Failed" ? "Failed" : "Success";
        setModalTitle(status === "Failed" ? "Error" : "Update Complete");
        setModalMsg(r.message || (status === "Failed" ? "Save failed." : "Saved successfully."));
        setModalStatus(status);
        setResultOpen(true);
      })
      .catch(err => {
        setModalTitle("Update Failed");
        setModalMsg(err.message||"Save failed.");
        setModalStatus("Failed");
        setResultOpen(true);
      });
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
    onSave && onSave();
  };

  return (
    <>
      <Overlay>
        <Modal colors={colors}>
          <form onSubmit={handleSaveClick}>
            <SectionTitle colors={colors}>Edit</SectionTitle>
            <SectionTitle style={{fontWeight: 'normal'}} colors={colors}>Implementation </SectionTitle>
            <ImplSection>
              <FieldRow>
              <Field colors={colors}>
                <label>E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label>Number of Students</label>
                <input
                  type="text"
                  value={students}
                  onChange={e => setStudents(e.target.value)}
                  required
                />
              </Field>
              </FieldRow>
              <Field colors={colors}>
                <label>School</label>
                <select
                  value={schoolId}
                  onChange={e => setSchoolId(e.target.value)}
                  required
                >
                  <option value="">— select school —</option>
                  {schools?.map(s => (
                    <option key={s.org_id} value={s.org_id}>
                      {s.org_display}
                    </option>
                  ))}
                </select>
              </Field>

              <Field colors={colors}>
                <label>School Level</label>
                <select
                  value={schoolLevel}
                  onChange={e => setSchoolLevel(e.target.value)}
                  required
                >
                  <option value="">— select school level —</option>
                  {schoolLevels?.map(level => {
                    const label = level.school_level_desc || level.school_level;
                    return (
                      <option key={level.school_level} value={level.school_level}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </Field>

              <Field colors={colors}>
                <GradesHeader colors={colors}>
                  <label>Grades</label>
                  <SelectAll
                    role="button"
                    tabIndex={0}
                    onClick={selectAllGrades}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectAllGrades();
                      }
                    }}
                  >
                    Select All
                  </SelectAll>
                </GradesHeader>
                <CheckboxGroup colors={colors}>
                  {gradeKeys?.map(g => (
                    <label key={g}>
                      <input
                        type="checkbox"
                        checked={gradesState[g]}
                        onChange={() => toggleGrade(g)}
                      />
                      {g}
                    </label>
                  ))}
                </CheckboxGroup>
              </Field>

              <FieldRow>
                <Field colors={colors}>
                  <label>Start Date</label>
                  <DateSelect>
                    {["year","month","day"].map(part=>(
                      <select
                        key={part}
                        value={startDate[part]}
                        onChange={e=>
                          setStartDate(sd=>({ ...sd, [part]: e.target.value }))
                        }
                      >
                    {part === "year"
                      ? startYearOptions.map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))
                      : Array.from({length: part==="month"?12:31},(_,i)=>i+1)
                              .map(v=>{
                                const val=String(v).padStart(2,"0");
                                return <option key={val} value={val}>{val}</option>;
                              })}
                  </select>
                    ))}
                  </DateSelect>
                </Field>
                <Field colors={colors}>
                  <label>End Date</label>
                  <DateSelect>
                    {["year","month","day"].map(part=>(
                      <select
                        key={part}
                        value={endDate[part]}
                        onChange={e=>
                          setEndDate(ed=>({ ...ed, [part]: e.target.value }))
                        }
                      >
                    {part === "year"
                      ? endYearOptions.map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))
                      : Array.from({length: part==="month"?12:31},(_,i)=>i+1)
                              .map(v=>{
                                const val=String(v).padStart(2,"0");
                                return <option key={val} value={val}>{val}</option>;
                              })}
                      </select>
                    ))}
                  </DateSelect>
                </Field>
              </FieldRow>
            </ImplSection>

            <SubmitRow>
              <BackButton type="button" onClick={onCancel} colors={colors}>
                Back
              </BackButton>
              <SaveButton type="submit" colors={colors}>
                Save
              </SaveButton>
            </SubmitRow>
          </form>
        </Modal>
      </Overlay>

      {/* Confirmation dialog */}
      <ConfirmationModal
        show={confirmOpen}
        title={modalTitle}
        message={modalMsg}
        onCancel={()=>setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      {/* Result dialog */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMsg}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
        single
      />
    </>
  );
}
